import { useEffect, useRef, useState } from 'react';
import { SyncPhase } from '../types/sync/enums';
import { isPhaseAtOrAfter } from '../utils/sync/syncPhase';

/**
 * Latches true once the sync's phase reaches `RelanceFill` — the per-episode "content may unblock"
 * signal shared by Home, the follow-up page's veil, and InitialSync.
 *
 * Latched rather than read live: a plain `phase === RelanceFill` check would flicker if any later
 * snapshot in the same episode ever reported an earlier phase (the terminal snapshot regressing to the
 * record default was exactly that bug — see `SyncService.CompleteWorkflow`). Once true it stays true
 * for the rest of the episode; the reset happens only on the next episode's own rising edge.
 */
export function useFillReached(syncing: boolean, phase: SyncPhase | null | undefined): boolean {
    const [fillReached, setFillReached] = useState(false);
    const wasSyncingRef = useRef(false);

    useEffect(() => {
        if (syncing && !wasSyncingRef.current) setFillReached(false);
        wasSyncingRef.current = syncing;
    }, [syncing]);

    useEffect(() => {
        if (isPhaseAtOrAfter(phase, SyncPhase.RelanceFill)) setFillReached(true);
    }, [phase]);

    return fillReached;
}
